import { PageLayout } from '../../layout/PageLayout'
import { RegionalPageLayout } from '../../layout/RegionalPageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { MapPageHeader } from '@app/components/blocks/pageHeader/MapPageHeader'
import {
  faPlus,
  faCheck,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { enrich } from '@i18n/localizeHelper'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { LockedPageCover } from '@app/components/blocks/lockedPageCover/LockedPageCover'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { getMunicipalityPageTitle } from './getPageTitle'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'
import {
  municipalityPageTabToUrlSegment,
  parseMunicipalityPageTab,
  resolveMunicipalityPageTab,
} from './parseMunicipalityPageTab'
import { RegionalPageHeader } from '@app/components/blocks/pageHeader/RegionalPageHeader'
import { RegionalHeaderScrollSentinel } from './regionalPage/RegionalHeaderScrollContext'
import { ContentSections } from '@app/components/blocks/contentSection/ContentSection'
import {
  mapPageContentBlocksToSections,
  PAGE_CONTENT_INITIATIVES_LIMIT,
  PAGE_CONTENT_PLOTS_LIMIT,
} from '@app/components/blocks/contentSection/mapPageContentBlocksToSections'
import { ThreadLatestViewModel } from 'functions/app/thread/view-latest'
import { MunicipalityPageTabContent } from './municipalityPage/MunicipalityPageTabContent'
import { municipalityTabs } from './constants'
import type { MunicipalityFeatures } from '@schema/featureToggle'
import { isMunicipalityLatestDiscussionsSectionAvailable } from './municipalityPage/isMunicipalityLayoutTabAvailable'

export type MunicipalityPageNewProps = {
  slug: string
  tab?: string
}

export const MunicipalityPageNew = ({
  slug,
  tab: tabParam,
}: MunicipalityPageNewProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const translations = useTranslations().app
  const municipalityQuery = useQuery('view-municipality', { slug })
  const { withAuthCheck } = useAuthModal()
  const client = useClient()
  const authState = useAuthState()

  useEffect(() => {
    setMounted(true)
  }, [])

  const tab = resolveMunicipalityPageTab(parseMunicipalityPageTab(tabParam))

  useEffect(() => {
    if (!tabParam) {
      return
    }

    const canonicalSegment =
      tab === municipalityTabs.overview
        ? undefined
        : municipalityPageTabToUrlSegment(tab)

    if (!canonicalSegment) {
      navigate(pages.municipalityPageNew(slug))
      return
    }

    if (tabParam !== canonicalSegment) {
      navigate(pages.municipalityPageNew(slug, tab))
    }
  }, [tabParam, tab, slug])

  const municipality: MunicipalityViewModel = municipalityQuery.data

  const showThreadSection = municipality
    ? isMunicipalityLatestDiscussionsSectionAvailable(municipality)
    : false

  const discussionsQuery = useQuery(
    showThreadSection ? 'view-latest-thread' : null,
    { id: municipality?.thread?.id },
  )
  const discussions: ThreadLatestViewModel = discussionsQuery.data ?? {
    posts: [],
  }

  const initiativeQuery = useQuery('list-regional-initiative', {
    municipalityId: municipality?.id,
    top: PAGE_CONTENT_INITIATIVES_LIMIT,
  })

  const plotQuery = useQuery('list-regional-plot', {
    municipalityId: municipality?.id,
    top: PAGE_CONTENT_PLOTS_LIMIT,
  })

  const overviewStoriesQuery = useQuery(
    municipality?.id ? 'list-regional-overview-stories' : null,
    { municipalityId: municipality?.id },
  )
  const overviewNewsQuery = useQuery(
    municipality?.id ? 'list-regional-overview-news' : null,
    { municipalityId: municipality?.id },
  )

  const contentSections = useMemo(
    () =>
      mapPageContentBlocksToSections(municipality?.contentBlocks, {
        stories: overviewStoriesQuery.data ?? [],
        news: overviewNewsQuery.data ?? [],
        threadPosts: discussions.posts ?? [],
        threadColumns: 3,
        showThreadSection,
        initiatives: initiativeQuery.data ?? [],
        plots: plotQuery.data ?? [],
        geographySlug: slug,
        geographyPage: 'municipality',
        titleLevel: 'H2',
      }),
    [
      municipality?.contentBlocks,
      overviewStoriesQuery.data,
      overviewNewsQuery.data,
      discussions.posts,
      showThreadSection,
      initiativeQuery.data,
      plotQuery.data,
      slug,
    ],
  )

  const pageManager = municipality?.pageManager
  const isHomeTab = tab === municipalityTabs.overview

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'municipality',
    entityIds: municipality?.id ? [municipality.id] : [],
  })
  const followerCount = (followerCountQuery.data as number[])?.[0] ?? 0

  const followMunicipality = useCallback(() => {
    if (!municipality?.id) return

    void withAuthCheck(async () => {
      await client.call('follow-municipality', {
        municipalityId: municipality.id,
      })
    })
  }, [withAuthCheck, client, municipality?.id])

  const isFollowingQuery = useQuery(authState?.userId ? 'is-following' : null, {
    entityType: 'municipality',
    entityId: municipality?.id,
    userId: Number(authState?.userId),
  })
  const isFollowing = mounted && ((isFollowingQuery.data as boolean) ?? false)
  const followButtonIcon = mounted ? (isFollowing ? faCheck : faPlus) : faPlus
  const followButtonLabel = mounted
    ? isFollowing
      ? translations.common.following
      : translations.common.follow
    : translations.common.follow

  const mapHeaderButtons = useMemo(
    () => [
      {
        label: followButtonLabel,
        onClick: followMunicipality,
        variant: 'primary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
        icon: followButtonIcon,
        iconPosition: 'left' as const,
      },
      {
        label: translations.common.share,
        onClick: () => setIsShareModalOpen(true),
        variant: 'secondary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
      },
    ],
    [
      translations.common.share,
      isMobile,
      followButtonLabel,
      followMunicipality,
      followButtonIcon,
    ],
  )

  const isMunicipalityLocked =
    !municipality?.crowdbuildingPartner &&
    !municipality?.region?.crowdbuildingPartner

  const municipalityPageStatus = getEntityPageStatus(
    municipalityQuery.loading,
    municipality,
  )

  if (municipalityPageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (municipalityPageStatus === 'loading') {
    return (
      <>
        <PageLayout gap="2rem">
          <PageSeo />
          <Row style={{ alignItems: 'flex-start' }} gap="3rem">
            <Column
              gap="1.5rem"
              alignItems="stretch"
              style={{ flex: '2 1 auto' }}
            >
              <MapPageHeader title={translations.common.loading} />
            </Column>
          </Row>
        </PageLayout>
      </>
    )
  }

  return (
    <>
      <RegionalPageLayout municipality={municipality} tab={tab}>
        <PageHead municipality={municipality} section={tab} />
        {isHomeTab ? (
          <Column gap={isMobile ? '2rem' : '5.5rem'} alignItems="stretch">
            <RegionalPageHeader
              title={municipality.name}
              subtitle={municipality.slogan}
              banner={municipality.backgroundImage}
              buttons={mapHeaderButtons}
              hasBadge={municipality.crowdbuildingPartner}
              profileRole={translations.regionPage.managedBy}
              profile={pageManager}
              followers={municipality.followers}
              totalFollowers={followerCount}
            />
            <RegionalHeaderScrollSentinel />
            <ContentSections sections={contentSections} />
          </Column>
        ) : (
          <MunicipalityPageTabContent
            municipality={municipality}
            municipalitySlug={slug}
            tab={tab}
          />
        )}
      </RegionalPageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={municipality.name}
      />

      {isMunicipalityLocked &&
        (isFollowing ? (
          <LockedPageCover
            title={translations.regionPage.lockedMunicipalityFollowed}
            body={translations.regionPage.lockedMunicipalityBodyFollowed}
            buttonProps={[
              {
                variant: 'secondary',
                size: 'medium',
                label: translations.common.back,
                onClick: () => navigate(pages.regions),
                icon: faArrowLeft,
                iconPosition: 'left',
              },
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.pageFollowed,
                onClick: followMunicipality,
                icon: faCheck,
                iconPosition: 'left',
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ) : (
          <LockedPageCover
            title={enrich(
              translations.regionPage.lockedMunicipalityHeader,
              municipality.name,
            )}
            body={enrich(
              translations.regionPage.lockedMunicipalityBody,
              municipality.name,
            )}
            buttonProps={[
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.followThePage,
                onClick: followMunicipality,
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ))}
    </>
  )
}

const PageHead = ({
  municipality,
  section,
}: {
  municipality: MunicipalityViewModel
  section: MunicipalityFeatures
}) => {
  const translations = useTranslations().app.regionPage
  const defaultPageTitle = getMunicipalityPageTitle(
    section,
    municipality.name,
    translations.pageTabTitle,
  )
  return (
    <PageSeo
      pageTitle={defaultPageTitle}
      description={municipality.content}
      meta={{
        metaTitle: municipality.metaTitle,
        metaDescription: municipality.metaDescription,
      }}
      image={municipality.image}
      noindex={shouldNoindexPageTab(section)}
    />
  )
}
